<template>
  <v-dialog
    v-model="modalOpen"
    max-width="800px"
    class="AddCompanyModal"
    scrollable
  >
    <v-card outlined>
      <v-card-title class="AddCompanyModal__title">
        <v-row>
          <v-col cols="auto" class="mr-auto">
            Add New Insurance Company
          </v-col>
          <v-col cols="auto">
            <v-btn icon @click="close">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text class="AddCompanyModal__content">
        <validation-observer v-slot="{ handleSubmit }">
          <form @submit.prevent="handleSubmit(onSubmit)" autocomplete="off">
            <!-- Company Name -->
            <v-row>
              <v-col cols="12">
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                  name="company_name"
                >
                  <v-text-field
                    label="Company Name *"
                    name="company_name"
                    outlined
                    dense
                    :hide-details="false"
                    :error="errors.length > 0"
                    :error-messages="errors"
                    v-model="formData.company_name"
                    :disabled="loading"
                  />
                </validation-provider>
              </v-col>
            </v-row>

            <!-- Greenway Aliases -->
            <v-row>
              <v-col cols="12">
                <div class="form-section">
                  <h4 class="section-title">Greenway Aliases</h4>
                  <div class="aliases-container">
                    <div v-for="(alias, index) in formData.greenway_aliases" :key="`greenway-${index}`" class="alias-row">
                      <v-text-field
                        v-model="formData.greenway_aliases[index]"
                        :label="`Alias ${index + 1}`"
                        outlined
                        dense
                        hide-details
                        :disabled="loading"
                        class="alias-field"
                      />
                      <v-btn
                        icon
                        small
                        @click="removeGreenwayAlias(index)"
                        :disabled="loading || formData.greenway_aliases.length <= 1"
                        class="remove-btn"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                    <v-btn
                      text
                      small
                      @click="addGreenwayAlias"
                      :disabled="loading"
                      class="add-btn"
                    >
                      <v-icon small left>mdi-plus</v-icon>
                      Add Greenway Alias
                    </v-btn>
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Ribbon Aliases -->
            <v-row>
              <v-col cols="12">
                <div class="form-section">
                  <h4 class="section-title">Ribbon Aliases</h4>
                  <div class="aliases-container">
                    <div v-for="(alias, index) in formData.ribbon_aliases" :key="`ribbon-${index}`" class="alias-row">
                      <v-text-field
                        v-model="formData.ribbon_aliases[index]"
                        :label="`Ribbon Alias ${index + 1}`"
                        outlined
                        dense
                        hide-details
                        :disabled="loading"
                        class="alias-field"
                      />
                      <v-btn
                        icon
                        small
                        @click="removeRibbonAlias(index)"
                        :disabled="loading || formData.ribbon_aliases.length <= 1"
                        class="remove-btn"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                    <v-btn
                      text
                      small
                      @click="addRibbonAlias"
                      :disabled="loading"
                      class="add-btn"
                    >
                      <v-icon small left>mdi-plus</v-icon>
                      Add Ribbon Alias
                    </v-btn>
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- PVerify Codes -->
            <v-row>
              <v-col cols="12">
                <div class="form-section">
                  <h4 class="section-title">PVerify Codes</h4>
                  <div class="aliases-container">
                    <div v-for="(code, index) in formData.pverify_codes" :key="`pverify-${index}`" class="alias-row">
                      <v-text-field
                        v-model="formData.pverify_codes[index]"
                        :label="`PVerify Code ${index + 1}`"
                        outlined
                        dense
                        hide-details
                        :disabled="loading"
                        class="alias-field"
                      />
                      <v-btn
                        icon
                        small
                        @click="removePverifyCode(index)"
                        :disabled="loading || formData.pverify_codes.length <= 1"
                        class="remove-btn"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                    <v-btn
                      text
                      small
                      @click="addPverifyCode"
                      :disabled="loading"
                      class="add-btn"
                    >
                      <v-icon small left>mdi-plus</v-icon>
                      Add PVerify Code
                    </v-btn>
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Allowable Amounts -->
            <v-row>
              <v-col cols="12">
                <div class="form-section">
                  <h4 class="section-title">Allowable Amounts</h4>
                  <div class="allowable-amounts-container">
                    <v-textarea
                      v-model="allowableAmountsJson"
                      label="Allowable Amounts (JSON format)"
                      outlined
                      rows="6"
                      :hide-details="false"
                      :disabled="loading"
                      :error="allowableAmountsError.length > 0"
                      :error-messages="allowableAmountsError"
                      placeholder='Example: {"customerID": {"cptCode": {"amount": 100.50, "cpt_code": "99213"}}}'
                      hint="Enter JSON object with nested structure: {customerID: {cptCode: {amount: number, cpt_code: string}}}"
                    />
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Action Buttons -->
            <v-row>
              <v-col>
                <div class="action-buttons">
                  <v-btn
                    color="primary"
                    type="submit"
                    :disabled="loading"
                    :loading="loading"
                    class="submit-btn"
                  >
                    Add Company
                  </v-btn>
                  <v-btn
                    text
                    @click="close"
                    :disabled="loading"
                    class="cancel-btn"
                  >
                    Cancel
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </form>
        </validation-observer>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';

export default {
  name: 'AddCompanyModal',
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formData: {
        company_name: '',
        greenway_aliases: [''],
        ribbon_aliases: [''],
        pverify_codes: [''],
        allowable_amounts: {},
      },
      allowableAmountsJson: '',
      allowableAmountsError: [],
    };
  },
  computed: {
    modalOpen: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  watch: {
    allowableAmountsJson(newValue) {
      this.validateAndParseAllowableAmounts(newValue);
    },
    modalOpen(newValue) {
      if (newValue) {
        this.resetForm();
      }
    },
  },
  methods: {
    close() {
      this.modalOpen = false;
      this.resetForm();
    },

    resetForm() {
      this.formData = {
        company_name: '',
        greenway_aliases: [''],
        ribbon_aliases: [''],
        pverify_codes: [''],
        allowable_amounts: {},
      };
      this.allowableAmountsJson = '';
      this.allowableAmountsError = [];
    },

    // Greenway Aliases methods
    addGreenwayAlias() {
      this.formData.greenway_aliases.push('');
    },

    removeGreenwayAlias(index) {
      if (this.formData.greenway_aliases.length > 1) {
        this.formData.greenway_aliases.splice(index, 1);
      }
    },

    // Ribbon Aliases methods
    addRibbonAlias() {
      this.formData.ribbon_aliases.push('');
    },

    removeRibbonAlias(index) {
      if (this.formData.ribbon_aliases.length > 1) {
        this.formData.ribbon_aliases.splice(index, 1);
      }
    },

    // PVerify Codes methods
    addPverifyCode() {
      this.formData.pverify_codes.push('');
    },

    removePverifyCode(index) {
      if (this.formData.pverify_codes.length > 1) {
        this.formData.pverify_codes.splice(index, 1);
      }
    },

    // Allowable Amounts validation
    validateAndParseAllowableAmounts(jsonString) {
      this.allowableAmountsError = [];

      if (!jsonString.trim()) {
        this.formData.allowable_amounts = {};
        return;
      }

      try {
        const parsed = JSON.parse(jsonString);
        this.formData.allowable_amounts = parsed;

        // Validate structure
        this.validateAllowableAmountsStructure(parsed);
      } catch (error) {
        this.allowableAmountsError = ['Invalid JSON format'];
        this.formData.allowable_amounts = {};
      }
    },

    validateAllowableAmountsStructure(obj) {
      if (typeof obj !== 'object' || obj === null) {
        this.allowableAmountsError = ['Allowable amounts must be an object'];
        return;
      }

      for (const customerId in obj) {
        const customerData = obj[customerId];
        if (typeof customerData !== 'object' || customerData === null) {
          this.allowableAmountsError = [`Customer ID "${customerId}" must contain an object`];
          return;
        }

        for (const cptCode in customerData) {
          const cptData = customerData[cptCode];
          if (typeof cptData !== 'object' || cptData === null) {
            this.allowableAmountsError = [`CPT code "${cptCode}" under customer "${customerId}" must be an object`];
            return;
          }

          if (!Object.prototype.hasOwnProperty.call(cptData, 'amount') || !Object.prototype.hasOwnProperty.call(cptData, 'cpt_code')) {
            this.allowableAmountsError = [`CPT data for "${cptCode}" must contain "amount" and "cpt_code" fields`];
            return;
          }

          if (typeof cptData.amount !== 'number') {
            this.allowableAmountsError = [`Amount for "${cptCode}" must be a number`];
            return;
          }
        }
      }
    },

    // Filter out empty strings from arrays
    filterEmptyStrings(arr) {
      return arr.filter(item => item && item.trim() !== '');
    },

    onSubmit() {
      // Validate allowable amounts one more time
      if (this.allowableAmountsError.length > 0) {
        return;
      }

      // Prepare form data
      const submitData = {
        company_name: this.formData.company_name.trim(),
        greenway_aliases: this.filterEmptyStrings(this.formData.greenway_aliases),
        ribbon_aliases: this.filterEmptyStrings(this.formData.ribbon_aliases),
        pverify_codes: this.filterEmptyStrings(this.formData.pverify_codes),
        allowable_amounts: this.formData.allowable_amounts,
      };

      // Emit the submit event
      this.$emit('submit', submitData);
    },
  },
};
</script>

<style lang="scss" scoped>
.AddCompanyModal {
  .AddCompanyModal__title {
    background: #f8f9fa;
    border-bottom: 1px solid #ddd;
  }

  .AddCompanyModal__content {
    padding: 1.5rem;
  }

  .form-section {
    margin-bottom: 2rem;

    .section-title {
      margin: 0 0 1rem 0;
      color: #333;
      font-weight: 500;
      font-size: 1rem;
      border-bottom: 1px solid #eee;
      padding-bottom: 0.5rem;
    }

    .aliases-container {
      .alias-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.5rem;

        .alias-field {
          flex: 1;
        }

        .remove-btn {
          flex-shrink: 0;
        }
      }

      .add-btn {
        margin-top: 0.5rem;
        text-transform: none;
      }
    }

    .allowable-amounts-container {
      .v-textarea {
        font-family: 'Courier New', monospace;
      }
    }
  }

  .action-buttons {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1rem;

    .submit-btn {
      text-transform: none;
    }

    .cancel-btn {
      text-transform: none;
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .AddCompanyModal {
    .form-section {
      .aliases-container {
        .alias-row {
          flex-direction: column;
          align-items: stretch;
          gap: 0.25rem;

          .remove-btn {
            align-self: flex-end;
            margin-top: 0.25rem;
          }
        }
      }
    }

    .action-buttons {
      flex-direction: column;

      .submit-btn,
      .cancel-btn {
        width: 100%;
      }
    }
  }
}
</style>